import React from 'react'
import { Link } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import {
    AccountRecord,
    ProfileViewWithShadowBan,
    DmFilter,
    useSearchShadowBannedProfilesQuery,
    useSetManualShadowBanForAccountMutation,
    useContentSearchQuery,
    useContentSearchCountQuery
} from '../../../../api/client'
import { canManageShadowBanningState } from '../../../../store/DomainStore'
import { formatCappedCount } from '../../../../util/searchCounts'
import ShadowBanConfirmationModal, { ShadowBanAction } from './ShadowBanConfirmationModal'
import { toastError, toastSuccess } from '../../../Notification'
import Spinner from '../../../Spinner'
import CommenterProfileImage from '../../../commenter/CommenterProfileImage'
import SimpleTable from '../../../table/SimpleTable'
import SimplePagination from '../../../table/SimplePagination'
import {
    FilterFn,
    PaginationState,
    createColumnHelper,
    getCoreRowModel,
    getPaginationRowModel,
    useReactTable
} from '@tanstack/react-table'
import ShadowBannedBadge from '../../../badges/ShadowBannedBadge'
import ProfileNameDisplay, {
    ProfileWithOptionalName,
    ProfileContentData
} from './ProfileNameDisplay'

type ProfileRowData = {
    contentData?: ProfileContentData
    commentCount?: number
    loading: boolean
}

/**
 * Hook to fetch content data and comment count for a profile.
 *
 * This retrieves the first content item to get author name and username,
 * as well as the total comment count for that profile on the given account.
 *
 *
 */
function useProfileData(accountId: number, profileId: number): ProfileRowData {
    const searchParams = {
        accountIds: [accountId],
        authorProfileIds: [profileId],
        dmFilter: DmFilter.CommentsOnly,
        timeDuration: 'P5Y'
    }

    const { data: contentData, loading: contentLoading } = useContentSearchQuery({
        variables: {
            params: {
                ...searchParams,
                maxResults: 1
            }
        }
    })

    const { data: countData, loading: countLoading } = useContentSearchCountQuery({
        variables: {
            params: searchParams
        }
    })

    const content = contentData?.contentSearch?.[0]

    return {
        contentData: content
            ? {
                  serviceAuthorName: content.serviceAuthorName,
                  serviceAuthorDisplayUsername: content.serviceAuthorDisplayUsername,
                  socialMediaServiceName: content.socialMediaServiceName
              }
            : undefined,
        commentCount: countData?.contentSearchCount,
        loading: contentLoading || countLoading
    }
}

type ProfileCellProps = {
    profile: ProfileWithOptionalName
    accountId: number
}

function ProfileCell({ profile, accountId }: ProfileCellProps) {
    const { contentData, loading } = useProfileData(accountId, profile.id)

    return (
        <div className="flex items-center gap-3">
            <div className="w-10 h-10">
                <CommenterProfileImage profile={profile} />
            </div>
            <div className="min-w-0">
                <div className="font-medium text-gray-900">
                    <ProfileNameDisplay
                        profile={profile}
                        contentData={contentData}
                        loading={loading}
                    />
                </div>
                <div className="flex gap-3">
                    {contentData?.socialMediaServiceName?.toLowerCase() === 'facebook' ? (
                        <span
                            className="text-sm text-gray-400 cursor-not-allowed"
                            data-tooltip-id="tooltip-id"
                            data-tooltip-content="Not currently supported"
                        >
                            View profile on Facebook
                        </span>
                    ) : (
                        <a
                            href={profile.profileUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-primary-600 hover:text-primary-500"
                        >
                            View profile on {contentData?.socialMediaServiceName ?? 'platform'}
                        </a>
                    )}
                    <Link
                        to={`/author/${profile.id}/account/${accountId}`}
                        className="text-sm text-primary-600 hover:text-primary-500"
                    >
                        View comments
                    </Link>
                </div>
            </div>
        </div>
    )
}

function CommentCountCell({ accountId, profileId }: { accountId: number; profileId: number }) {
    const { commentCount, loading } = useProfileData(accountId, profileId)

    if (loading) {
        return <span className="text-gray-400">...</span>
    }

    return <span>{commentCount === undefined ? '—' : formatCappedCount(commentCount)}</span>
}

type ActionCellProps = {
    entry: ProfileViewWithShadowBan
    accountId: number
    isUpdating: boolean
    canManageShadowBanning: boolean
    onOpenConfirm: (entry: ProfileViewWithShadowBan, contentData?: ProfileContentData) => void
}

function ActionCell({
    entry,
    accountId,
    isUpdating,
    canManageShadowBanning,
    onOpenConfirm
}: ActionCellProps) {
    const { contentData } = useProfileData(accountId, entry.id)
    const isDisabled = entry.shadowBanStatusCode === 0

    return (
        <button
            type="button"
            onClick={canManageShadowBanning ? () => onOpenConfirm(entry, contentData) : undefined}
            disabled={isUpdating || !canManageShadowBanning}
            className={`text-sm font-semibold text-primary-600 hover:text-primary-500 disabled:text-gray-400 ${!canManageShadowBanning ? 'cursor-not-allowed' : ''}`}
            data-tooltip-id="tooltip-id"
            data-tooltip-content={
                !canManageShadowBanning
                    ? 'Contact support@arwen.ai to enable shadow banning'
                    : undefined
            }
        >
            {isUpdating ? <Spinner size={4} /> : isDisabled ? 'Enable ban' : 'Disable ban'}
        </button>
    )
}

const columnHelper = createColumnHelper<ProfileViewWithShadowBan>()
const FETCH_LIMIT = 200

export function ShadowBannedProfilesTable({ account }: { account: AccountRecord }) {
    const [canManageShadowBanning] = useRecoilState(canManageShadowBanningState)
    const [pagination, setPagination] = React.useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10
    })
    const [updatingProfileId, setUpdatingProfileId] = React.useState<number | null>(null)
    const [confirmProfile, setConfirmProfile] = React.useState<ProfileViewWithShadowBan | null>(
        null
    )
    const [confirmAction, setConfirmAction] = React.useState<ShadowBanAction | null>(null)
    const [confirmOpen, setConfirmOpen] = React.useState(false)
    const [confirmContentData, setConfirmContentData] = React.useState<
        ProfileContentData | undefined
    >(undefined)

    const { data, loading, refetch } = useSearchShadowBannedProfilesQuery({
        fetchPolicy: 'no-cache',
        variables: { accountId: account.id, limit: FETCH_LIMIT, offset: 0 },
        onError: (error) => {
            console.error(error)
            toastError('Failed to load shadow banned profiles. Please try again.')
        }
    })
    const [setManualShadowBanForAccount] = useSetManualShadowBanForAccountMutation()

    const profiles = data?.searchShadowBannedProfiles ?? []

    const handleManualShadowBanUpdate = React.useCallback(
        async (profileId: number, action: ShadowBanAction) => {
            setUpdatingProfileId(profileId)
            const enableBan = action === 'enable'
            try {
                await setManualShadowBanForAccount({
                    variables: {
                        accountId: account.id,
                        profileId,
                        contentId: undefined,
                        enabled: enableBan
                    }
                })
                await refetch()
                toastSuccess(
                    enableBan
                        ? 'Shadow ban enabled successfully.'
                        : 'Shadow ban removed successfully.'
                )
            } catch (error) {
                console.error(error)
                toastError(
                    enableBan
                        ? 'Failed to enable shadow ban. Please try again.'
                        : 'Failed to remove shadow ban. Please try again.'
                )
            } finally {
                setUpdatingProfileId(null)
            }
        },
        [account.id, refetch, setManualShadowBanForAccount]
    )

    const openConfirm = React.useCallback(
        (entry: ProfileViewWithShadowBan, contentData?: ProfileContentData) => {
            setConfirmProfile(entry)
            setConfirmAction(entry.shadowBanStatusCode === 0 ? 'enable' : 'disable')
            setConfirmContentData(contentData)
            setConfirmOpen(true)
        },
        []
    )

    const closeConfirm = React.useCallback(() => {
        if (updatingProfileId !== null) {
            return
        }
        setConfirmOpen(false)
        setConfirmProfile(null)
        setConfirmAction(null)
        setConfirmContentData(undefined)
    }, [updatingProfileId])

    const confirmShadowBanAction = React.useCallback(async () => {
        if (!confirmProfile || !confirmAction) {
            return
        }
        await handleManualShadowBanUpdate(confirmProfile.id, confirmAction)
        setConfirmOpen(false)
        setConfirmProfile(null)
        setConfirmAction(null)
        setConfirmContentData(undefined)
    }, [confirmAction, confirmProfile, handleManualShadowBanUpdate])

    const columns = React.useMemo(
        () => [
            columnHelper.display({
                id: 'profile',
                header: 'Profile',
                cell: ({ row }) => {
                    const profile = row.original as ProfileWithOptionalName
                    return <ProfileCell profile={profile} accountId={account.id} />
                }
            }),
            columnHelper.display({
                id: 'comments',
                header: 'Comments',
                cell: ({ row }) => {
                    return <CommentCountCell accountId={account.id} profileId={row.original.id} />
                }
            }),
            // columnHelper.accessor((row) => row.profile.serviceProfileId, {
            //     id: 'serviceProfileId',
            //     header: 'Service ID',
            //     cell: (info) => info.getValue()
            // }),
            // The following are only available for Instagram and even then they may well be out of date. Omit for now.
            // columnHelper.accessor((row) => row.profile.followers, {
            //     id: 'followers',
            //     header: 'Followers',
            //     cell: (info) => {
            //         const value = info.getValue()
            //         return typeof value === 'number' ? value.toLocaleString() : '—'
            //     }
            // }),
            // columnHelper.accessor((row) => row.profile.following, {
            //     id: 'following',
            //     header: 'Following',
            //     cell: (info) => {
            //         const value = info.getValue()
            //         return typeof value === 'number' ? value.toLocaleString() : '—'
            //     }
            // }),
            // columnHelper.accessor((row) => row.profile.posts, {
            //     id: 'posts',
            //     header: 'Posts',
            //     cell: (info) => {
            //         const value = info.getValue()
            //         return typeof value === 'number' ? value.toLocaleString() : '—'
            //     }
            // }),
            columnHelper.accessor('shadowBanStatus', {
                header: 'Status',
                cell: (info) => {
                    const isActive = info.row.original.shadowBanStatusCode === 1
                    if (isActive) {
                        return <ShadowBannedBadge className="px-2 py-1" />
                    }
                    return (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            Shadow Banning Disabled
                        </span>
                    )
                }
            }),
            columnHelper.display({
                id: 'actions',
                header: '',
                cell: ({ row }) => {
                    const entry = row.original
                    const isUpdating = updatingProfileId === entry.id
                    return (
                        <ActionCell
                            entry={entry}
                            accountId={account.id}
                            isUpdating={isUpdating}
                            canManageShadowBanning={canManageShadowBanning}
                            onOpenConfirm={openConfirm}
                        />
                    )
                }
            })
        ],
        [account.id, canManageShadowBanning, openConfirm, updatingProfileId]
    )
    const table = useReactTable({
        data: profiles,
        columns,
        filterFns: {} as Record<string, FilterFn<ProfileViewWithShadowBan>>,
        state: { pagination },
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    })

    const hasProfiles = profiles.length > 0

    return (
        <div className="rounded-lg border border-gray-200 shadow-sm">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <div>
                    <h3 className="text-base font-semibold text-gray-900">
                        Shadow banned commenters
                    </h3>
                    <p className="text-sm text-gray-600">
                        Commenters who are currently shadow banned by Arwen.
                    </p>
                </div>
                {loading && (
                    <div className="text-gray-500">
                        <Spinner size={5} />
                    </div>
                )}
            </div>
            <div className="px-4 py-4">
                {loading && !hasProfiles ? (
                    <div className="flex justify-center py-6">
                        <Spinner size={6} />
                    </div>
                ) : !hasProfiles ? (
                    <p className="text-sm text-gray-500">No profiles are shadow banned.</p>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <SimpleTable table={table} />
                        </div>
                        {profiles.length > pagination.pageSize && (
                            <div className="pt-4">
                                <SimplePagination table={table} />
                            </div>
                        )}
                    </>
                )}
            </div>
            <ShadowBanConfirmationModal
                open={confirmOpen}
                profile={confirmProfile}
                action={confirmAction ?? 'disable'}
                loading={updatingProfileId !== null}
                onCancel={closeConfirm}
                onConfirm={confirmShadowBanAction}
                contentData={confirmContentData}
            />
        </div>
    )
}
